<template>
	<div class="home-page">
		<!-- 顶部导航栏 -->
		<NavBar />

		<!-- 背景装饰 -->
		<div class="bg-decoration">
			<div class="bg-circle bg-circle-1"></div>
			<div class="bg-circle bg-circle-2"></div>
			<div class="bg-circle bg-circle-3"></div>
		</div>

		<!-- 浮动元素 -->
		<div class="floating-elements" ref="floatingElements"></div>

		<!-- Hero Section -->
		<section class="hero-section">
			<div class="hero-container">
				<div class="hero-badge">
					<span class="hero-badge-dot"></span>
					<span>中国-东盟合作建筑AI教学平台</span>
				</div>

				<div class="hero-mascot">
					<div class="mascot-container">
						<div class="mascot-text">CET</div>
					</div>
				</div>

				<h1 class="hero-title">中国-东盟建筑工程技术专业AI教学助手</h1>
				<h1 class="hero-title hero-title1">AI Teaching Assistant for China ASEAN Construction Engineering
					Technology
					Major</h1>

				<!-- 东盟国家国旗部分 -->
				<div class="asean-flags-section">
					<div class="flags-container">
						<div class="flags-title">中国-东盟合作伙伴</div>

						<!-- 所有国旗在一排 -->
						<div class="all-flags-wrapper">
							<!-- 中国国旗 -->
							<div class="flag-item china-flag">
								<div class="flag-img">
									<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
										<rect width="60" height="40" fill="#DE2910" />
										<polygon
											points="10,5 11.5,9.5 16,9.5 12.5,12 14,16.5 10,13.5 6,16.5 7.5,12 4,9.5 8.5,9.5"
											fill="#FFDE00" />
										<polygon
											points="20,2 20.5,3.5 22,3.5 20.75,4.5 21.25,6 20,5 18.75,6 19.25,4.5 18,3.5 19.5,3.5"
											fill="#FFDE00" transform="rotate(20 20 8)" />
										<polygon
											points="24,8 24.5,9.5 26,9.5 24.75,10.5 25.25,12 24,11 22.75,12 23.25,10.5 22,9.5 23.5,9.5"
											fill="#FFDE00" transform="rotate(40 24 14)" />
										<polygon
											points="24,16 24.5,17.5 26,17.5 24.75,18.5 25.25,20 24,19 22.75,20 23.25,18.5 22,17.5 23.5,17.5"
											fill="#FFDE00" transform="rotate(0 24 18)" />
										<polygon
											points="20,20 20.5,21.5 22,21.5 20.75,22.5 21.25,24 20,23 18.75,24 19.25,22.5 18,21.5 19.5,21.5"
											fill="#FFDE00" transform="rotate(-20 20 22)" />
									</svg>
								</div>
								<span class="flag-name">中国</span>
								<span class="flag-name-en">China</span>
							</div>

							<!-- 东盟10国国旗 -->
							<div class="flag-item" v-for="country in aseanCountries" :key="country.code">
								<div class="flag-img" v-html="country.flagSvg"></div>
								<span class="flag-name">{{ country.name }}</span>
								<span class="flag-name-en">{{ country.nameEn }}</span>
							</div>
						</div>
					</div>

					<div class="flags-subtitle">
						携手东盟十国，共建国际化建筑工程技术教学平台
					</div>
				</div>

				<p class="hero-subtitle">融合中国标准与东盟规范，为区域建筑工程技术人才培养提供智能化教学支持</p>
				<p class="hero-subtitle hero-subtitle1">Integrating Chinese standards and ASEAN norms to provide
					intelligent
					teaching support for the cultivation of regional construction engineering technical talents</p>
			</div>
		</section>

		<!-- Chat Section -->
		<section class="chat-section">
			<div class="chat-container">
				<div class="chat-input-wrapper">
					<textarea class="chat-input" placeholder="Go and ask questions" rows="1" v-model="chatMessage"
						@input="adjustTextareaHeight" @keydown.enter.prevent="handleEnterKey"
						ref="chatInput"></textarea>
					<button class="send-button" @click="sendMessage">
						<svg viewBox="0 0 24 24">
							<path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
						</svg>
					</button>
				</div>
			</div>
		</section>

		<!-- Quick Actions -->
		<section class="quick-actions-section">
			<div class="section-header">
				<span class="section-header1 section-header3">⚡</span>
				<div>
					<h2 class="section-title">

						<div class="section-title1">
							<span>快速开始</span>
							<span>Quick start</span>
						</div>
					</h2>
					<p class="section-subtitle">选择您感兴趣的话题，快速获得专业解答</p>
					<p class="section-subtitle">Choose a topic that interests you and quickly get professional answers
					</p>
				</div>
			</div>
			<div class="quick-actions-grid">
				<div class="quick-action-card" v-for="action in quickActions" :key="action.id"
					@click="selectQuickAction(action.title)">
					<div class="quick-action-icon">{{ action.icon }}</div>
					<div class="quick-action-text">
						<div class="quick-action-title">{{ action.title }}</div>
						<div class="quick-action-title quick-action-title1">{{ action.ehlis }}</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Core Features -->
		<section class="features-section">
			<div class="features-container">
				<div class="section-header">
					<span class="section-header1 section-header2">🚀</span>
					<div>
						<h2 class="section-title">

							<div class="section-title1">
								<span>核心功能</span>
								<span>Core functions</span>
							</div>
						</h2>
						<p class="section-subtitle">全面覆盖建筑工程技术专业教学需求，助力国际化人才培养</p>
						<p class="section-subtitle">Fully covering the teaching needs of the construction engineering
							technology
							major
							and assisting in the cultivation of international talents</p>
					</div>
				</div>
				<div class="features-grid">
					<div class="feature-card" :class="{ highlight: feature.highlight }" v-for="feature in features"
						:key="feature.id" @click="lesaon(feature)">
						<div class="feature-icon">{{ feature.icon }}</div>
						<h3 class="feature-title">{{ feature.title }}</h3>
						<h3 class="feature-title feature-title1">{{ feature.ehlis }}</h3>
						<p class="feature-description">{{ feature.description }}</p>
						<span class="feature-tag" v-if="feature.tag">{{ feature.tag }}</span>
					</div>
				</div>
			</div>
		</section>

		<!-- Expert System Section -->
		<section class="expert-system-section">
			<div class="expert-header">
				<h2 class="expert-title">
					中国-东盟建筑工程技术专业

				</h2>
				<h2 class="expert-title expert-title1">
					China ASEAN Construction Engineering Technology Major

				</h2>
				<span class="expert-title-gradient">混合专家系统</span>
				<p class="expert-subtitle">
					CET_HybridExpertSystem
					<span class="version-badge">V3.0</span>
				</p>
			</div>

			<div class="stats-container">
				<div class="stats-grid" ref="statsGrid">
					<div class="stat-card" v-for="stat in stats" :key="stat.id">
						<div class="stat-number">{{ stat.currentValue }}</div>
						<div class="stat-label">{{ stat.label }}</div>
						<div class="stat-label">{{ stat.ehlis }}</div>
					</div>
				</div>
			</div>

			<div class="architecture-container">
				<div class="architecture-grid">
					<div class="library-card" v-for="library in libraries" :key="library.id">
						<div class="library-icon">{{ library.icon }}</div>
						<h3 class="library-title">{{ library.title }}</h3>
						<h3 class="library-title library-title1">{{ library.ehlis }}</h3>
						<p class="library-description">{{ library.description }}</p>
					</div>
				</div>
			</div>
		</section>

		<!-- Data Flow Section -->
		<section class="data-flow-section">
			<div class="data-flow-container">
				<h2 class="flow-title">YIJING AI 中国-东盟智能计算过程</h2>
				<h2 class="flow-title flow-title1">China ASEAN Intelligent Computing Process</h2>
				<div class="flow-diagram">
					<template v-for="(step, index) in flowSteps">
						<div class="flow-step" :key="`step-${step.id}`">
							<span class="flow-step-icon">{{ step.icon }}</span>
							<h3 class="flow-step-title">{{ step.title }}</h3>
							<h3 class="flow-step-title flow-step-title1">{{ step.ehlis }}</h3>
							<p class="flow-step-desc" v-html="step.description"></p>
						</div>
						<div class="flow-arrow" v-if="index < flowSteps.length - 1" :key="`arrow-${step.id}`"></div>
					</template>
				</div>
			</div>
		</section>

		<!-- Detailed Structure -->
		<section class="structure-section">
			<div class="structure-container">

				<div class="section-header">
					<span class="section-header1">📚</span>
					<div>
						<h2 class="section-title">
							<div class="section-title1">
								<span>详细架构展示</span>
								<span>Detailed Architecture Display</span>
							</div>
						</h2>
						<p class="section-subtitle">三层知识体系，构建中国-东盟建筑工程技术专业智能教学平台</p>
						<p class="section-subtitle">Three layer knowledge system, constructing an intelligent teaching
							platform for
							China
							ASEAN architectural engineering technology majors</p>
					</div>
				</div>
				<div class="structure-grid">
					<div class="structure-card" v-for="structure in structures" :key="structure.id">
						<div class="structure-header">
							<span>{{ structure.icon }}</span>
							<span>{{ structure.title }}</span>
						</div>
						<div class="subsection" v-for="subsection in structure.subsections" :key="subsection">
							{{ subsection }}</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Download Section -->
		<section class="download-section">
			<div class="download-container">
				<h2 class="download-title">免费体验中国-东盟建筑AI教学平台</h2>
				<h2 class="download-title download-title1">Free trial of China ASEAN architecture AI teaching platform
				</h2>
				<p class="download-subtitle">立即开启国际化建筑工程技术教学新体验</p>
				<p class="download-subtitle download-subtitle1">Start a new experience of international construction
					engineering
					technology teaching immediately</p>

				<div class="download-content">
					<div class="download-badge">国际合作版</div>

					<div class="download-features">
						<div class="download-feature" v-for="feature in downloadFeatures" :key="feature">
							<div class="download-feature-icon">✓</div>
							<div class="download-feature-text">{{ feature }}</div>
						</div>
					</div>

					<div class="download-cta">
						<a href="#wechat-contact" class="download-button" @click="scrollToWechat">
							<span class="download-button-icon">🌏</span>
							<span>立即免费试用</span>
						</a>

						<div class="wechat-info" id="wechat-contact">
							<div class="wechat-qr">
								<div class="wechat-qr-placeholder">
									<img src="@/assets/image/wx.png" alt="AI助手" />
								</div>
							</div>
							<div class="wechat-text">
								<div class="wechat-title">扫码添加微信，获取国际版试用</div>
								<div class="wechat-id">联系电话：400-886-0313<br />
									联系人：尚老师 13003209505<br />
									E-mail：13003209505@163.com</div>
								<div class="wechat-desc">
									支持中文、English<br>
									专业顾问将在24小时内与您联系
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
	import NavBar from '@/components/common/NavBar.vue'
	export default {
		name: 'HomePage',
		components: {
			NavBar,
		},
		data() {
			return {
				chatMessage: '',
				recentConversations: [],
				userPhone: '', // 用户手机号
				// 东盟10国数据
				aseanCountries: [{
						code: 'ID',
						name: '印尼',
						nameEn: 'Indonesia',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="20" fill="#FF0000"/><rect y="20" width="60" height="20" fill="#FFFFFF"/></svg>'
					},
					{
						code: 'MY',
						name: '马来',
						nameEn: 'Malaysia',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><defs><clipPath id="my-moon"><circle cx="25" cy="20" r="8"/></clipPath></defs><rect width="60" height="40" fill="#CC0001"/><rect width="60" height="3.08" fill="#FFFFFF" y="0"/><rect width="60" height="3.08" fill="#FFFFFF" y="6.15"/><rect width="60" height="3.08" fill="#FFFFFF" y="12.31"/><rect width="60" height="3.08" fill="#FFFFFF" y="18.46"/><rect width="60" height="3.08" fill="#FFFFFF" y="24.62"/><rect width="60" height="3.08" fill="#FFFFFF" y="30.77"/><rect width="60" height="3.08" fill="#FFFFFF" y="36.92"/><rect width="30" height="22" fill="#010066"/><circle cx="20" cy="11" r="6" fill="#FFCC00"/><circle cx="22" cy="11" r="5" fill="#010066"/><polygon points="25,7 26,10 29,10 26.5,12 27.5,15 25,13 22.5,15 23.5,12 21,10 24,10" fill="#FFCC00"/></svg>'
					},
					{
						code: 'PH',
						name: '菲律宾',
						nameEn: 'Philippines',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="20" fill="#0038A8"/><rect y="20" width="60" height="20" fill="#CE1126"/><path d="M 0,0 L 20,20 L 0,40 Z" fill="#FFFFFF"/><circle cx="6" cy="20" r="3" fill="#FCD116" stroke="#000000" stroke-width="0.1"/><polygon points="14,20 14.5,18 16,18.5 15,17.5 16,16.5 14.5,17 14,15 13.5,17 12,16.5 13,17.5 12,18.5 13.5,18" fill="#FCD116"/><polygon points="6,10 6.3,11 7.3,10.7 6.5,11.5 7,12.5 6,12 5,12.5 5.5,11.5 4.7,10.7 5.7,11" fill="#FCD116"/><polygon points="6,30 6.3,29 7.3,29.3 6.5,28.5 7,27.5 6,28 5,27.5 5.5,28.5 4.7,29.3 5.7,29" fill="#FCD116"/></svg>'
					},
					{
						code: 'SG',
						name: '新加坡',
						nameEn: 'Singapore',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="20" fill="#ED2939"/><rect y="20" width="60" height="20" fill="#FFFFFF"/><circle cx="15" cy="10" r="6" fill="#FFFFFF"/><circle cx="17" cy="10" r="5" fill="#ED2939"/><polygon points="20,6 20.3,7 21.3,6.7 20.5,7.5 21,8.5 20,8 19,8.5 19.5,7.5 18.7,6.7 19.7,7" fill="#FFFFFF"/><polygon points="23,8 23.3,9 24.3,8.7 23.5,9.5 24,10.5 23,10 22,10.5 22.5,9.5 21.7,8.7 22.7,9" fill="#FFFFFF"/><polygon points="23,12 23.3,13 24.3,12.7 23.5,13.5 24,14.5 23,14 22,14.5 22.5,13.5 21.7,12.7 22.7,13" fill="#FFFFFF"/><polygon points="20,14 20.3,15 21.3,14.7 20.5,15.5 21,16.5 20,16 19,16.5 19.5,15.5 18.7,14.7 19.7,15" fill="#FFFFFF"/><polygon points="17,12 17.3,13 18.3,12.7 17.5,13.5 18,14.5 17,14 16,14.5 16.5,13.5 15.7,12.7 16.7,13" fill="#FFFFFF"/></svg>'
					},
					{
						code: 'TH',
						name: '泰国',
						nameEn: 'Thailand',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="6.67" fill="#A51931"/><rect y="6.67" width="60" height="6.67" fill="#F4F5F8"/><rect y="13.33" width="60" height="13.33" fill="#2D2A4A"/><rect y="26.67" width="60" height="6.67" fill="#F4F5F8"/><rect y="33.33" width="60" height="6.67" fill="#A51931"/></svg>'
					},
					{
						code: 'VN',
						name: '越南',
						nameEn: 'Vietnam',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="40" fill="#DA251D"/><polygon points="30,10 34,22 22,15 38,15 26,22" fill="#FFFF00"/></svg>'
					},
					{
						code: 'BN',
						name: '文莱',
						nameEn: 'Brunei',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="40" fill="#F7E017"/><path d="M 0,10 L 60,25 L 60,15 L 0,0 Z" fill="#FFFFFF"/><path d="M 0,30 L 60,15 L 60,25 L 0,40 Z" fill="#FFFFFF"/><path d="M 0,10 L 60,25 L 60,20 L 0,5 Z" fill="#000000"/><path d="M 0,35 L 60,20 L 60,25 L 0,40 Z" fill="#000000"/><g transform="translate(30,20)"><circle r="8" fill="#CE1126"/><g fill="#F7E017"><rect x="-2" y="-6" width="4" height="8"/><rect x="-4" y="-2" width="8" height="2"/><path d="M -3,-8 Q 0,-10 3,-8 L 2,-4 L -2,-4 Z"/></g></g></svg>'
					},
					{
						code: 'KH',
						name: '柬埔寨',
						nameEn: 'Cambodia',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="10" fill="#032EA1"/><rect y="10" width="60" height="20" fill="#E00025"/><rect y="30" width="60" height="10" fill="#032EA1"/><g transform="translate(30,20)" fill="#FFFFFF"><rect x="-8" y="-6" width="16" height="10"/><rect x="-6" y="-8" width="3" height="2"/><rect x="-1.5" y="-8" width="3" height="2"/><rect x="3" y="-8" width="3" height="2"/><path d="M -5,4 L -5,6 L 5,6 L 5,4 Z"/></g></svg>'
					},
					{
						code: 'LA',
						name: '老挝',
						nameEn: 'Laos',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="10" fill="#CE1126"/><rect y="10" width="60" height="20" fill="#002868"/><rect y="30" width="60" height="10" fill="#CE1126"/><circle cx="30" cy="20" r="7" fill="#FFFFFF"/></svg>'
					},
					{
						code: 'MM',
						name: '缅甸',
						nameEn: 'Myanmar',
						flagSvg: '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="13.33" fill="#FECB00"/><rect y="13.33" width="60" height="13.33" fill="#34B233"/><rect y="26.67" width="60" height="13.33" fill="#EA2839"/><polygon points="30,8 34,20 22,13 38,13 26,20" fill="#FFFFFF" transform="scale(1.5) translate(10,3)"/></svg>'
					}
				],
				quickActions: [{
						id: 1,
						icon: '🏗️',
						title: '中国与东盟建筑施工技术标准对比',
						ehlis: "Comparison of Construction Technology Standards between China and ASEAN"
					},
					{
						id: 2,
						icon: '📊',
						title: '生成建筑工程技术教学PPT',
						ehlis: "Generate PPT for teaching construction engineering technology"
					},
					{
						id: 3,
						icon: '🌏',
						title: '东盟国家建筑工程师认证要求',
						ehlis: "Certification Requirements for Architectural Engineers in ASEAN Countries"
					},
					{
						id: 4,
						icon: '🔬',
						title: '建筑材料检测技术规范',
						ehlis: "Technical Specification for Testing of Building Materials"
					}
				],
				features: [{
						id: 1,
						icon: '⚡',
						title: '一键备课系统',
						ehlis: "One click lesson preparation system",
						description: '上传中文或英文教材，3分钟生成多语言PPT、教学设计、实训指导等教学资源',
						tag: '支持多语言 · 智能翻译'
					},
					{
						id: 2,
						icon: '🌏',
						title: '国际化教学支持',
						ehlis: "International teaching support",
						description: '整合中国与东盟各国建筑标准，提供跨文化、多语言的教学内容'
					},
					{
						id: 3,
						icon: '🏗️',
						title: 'BIM可视化教学',
						ehlis: "BIM visualization teaching",
						description: '3D建筑模型展示，施工工艺动画，虚拟现场仿真教学'
					},
					{
						id: 4,
						icon: '🤝',
						title: '中国-东盟建筑企业合作',
						ehlis: "China ASEAN Construction Enterprise Cooperation",
						description: '与中建集团、中铁建工等中国企业及东盟主要建筑公司合作，提供真实项目案例',
						highlight: true
					},
					{
						id: 5,
						icon: '📊',
						title: '智能检测与评估',
						ehlis: "Intelligent detection and evaluation",
						description: 'AI驱动的学习进度跟踪，个性化学习路径推荐，多维度能力评估'
					},
					{
						id: 6,
						icon: '🎓',
						title: '产教融合实训',
						ehlis: "Integration of Industry and Education Training",
						description: '结合"一带一路"建设项目，提供国际工程实践机会和认证培训'
					}
				],
				stats: [{
						id: 1,
						label: '行业标准库',
						targetValue: 7,
						currentValue: 0,
						ehlis: "Industry standard library module",
					},
					{
						id: 2,
						label: '专业教学库',
						targetValue: 892,
						currentValue: 0,
						ehlis: "Professional Teaching Library Courses",
					},
					{
						id: 3,
						label: '个性化定制库',
						targetValue: 5,
						currentValue: 0,
						ehlis: "Personalized customization library type",
					}
				],
				libraries: [{
						id: 1,
						icon: '🏗️',
						title: '00_公有库',
						ehlis: "Public Library",
						description: '按行业全产业链归纳，涵盖政策法规、建筑材料、建筑设计、施工技术、施工管理、测量检测、BIM应用等7大核心模块，融合中国标准与东盟规范。'
					},
					{
						id: 2,
						icon: '🎓',
						title: '01_建筑工程技术专业库',
						ehlis: "Architectural Engineering Technology Professional Library",
						description: '按课程体系归纳，包含专业基础、核心课程、拓展课程和实训课程四大板块，涵盖30门专业课程，支持中英文双语教学。'
					},
					{
						id: 3,
						icon: '👤',
						title: '02_建筑工程技术私有库',
						ehlis: "Private Library of Construction Engineering Technology",
						description: '按用户内容自定义，支持项目案例、技术资料、标准规范、软件工具、学习笔记等个性化知识管理，实现跨文化知识积累。'
					}
				],
				flowSteps: [{
						id: 1,
						icon: '🌏',
						title: '多国标准整合',
						ehlis: "Multi country standard integration",
						description: '中国建筑标准<br>东盟各国规范<br>国际工程案例'
					},
					{
						id: 2,
						icon: '🧠',
						title: '多语言AI处理',
						ehlis: "Multi language AI processing",
						description: '中英泰越多语言<br>智能翻译转换<br>跨文化理解'
					},
					{
						id: 3,
						icon: '☁️',
						title: '区域云存储',
						ehlis: "Regional cloud storage",
						description: '中国-东盟数据中心<br>合规安全存储<br>跨境数据流通'
					},
					{
						id: 4,
						icon: '🚀',
						title: '国际化AI服务',
						ehlis: "International AI Services",
						description: '智能教学助手<br>跨文化推荐<br>国际认证支持'
					}
				],
				structures: [{
						id: 1,
						icon: '🏗️',
						title: '00_公有库（按行业全产业链归纳）',
						subsections: [
							'0001_政策法规库 - 国家法律法规、行业技术标准、地方政策规范、国际标准规范',
							'0002_建筑材料库 - 结构材料、装饰材料、保温材料、防水材料',
							'0003_建筑设计库 - 建筑制图与CAD、建筑构造设计、结构设计基础、建筑设备设计',
							'0004_施工技术库 - 地基基础施工、主体结构施工、装饰装修施工、屋面防水施工',
							'0005_施工管理库 - 施工组织管理、进度控制管理、质量控制管理、安全控制管理',
							'0006_测量检测库 - 工程测量技术、质量检测技术、安全检测技术、变形监测技术',
							'0007_BIM应用库 - BIM建模技术、BIM施工应用、BIM管理应用、BIM协同平台'
						]
					},
					{
						id: 2,
						icon: '🎓',
						title: '01_建筑工程技术专业库（按课程体系归纳）',
						subsections: [
							'0101_专业基础课程知识库 - 建筑材料、建筑制图与CAD、建筑构造等8门',
							'0102_专业核心课程知识库 - 建筑施工技术、建筑施工组织、质量与安全管理等6门',
							'0103_专业拓展课程知识库 - 建筑节能、BIM建模、装配式建筑等13门',
							'0104_实训课程知识库 - 施工工艺实训、造价实训、BIM应用实训等8个实训'
						]
					},
					{
						id: 3,
						icon: '👤',
						title: '02_建筑工程技术私有库（按用户内容自定义）',
						subsections: [
							'0201_用户项目案例库 - 住宅建筑、公共建筑、工业建筑、装配式建筑等',
							'0202_用户技术资料库 - 施工图纸、施工方案、质量检测、安全管理等',
							'0203_用户标准规范库 - 企业标准、地方规范、国际标准、行业最新标准',
							'0204_用户软件工具库 - 施工管理软件、造价计算工具、BIM应用工具等',
							'0205_用户学习笔记库 - 学习心得、技术总结、问题解决方案等'
						]
					}
				],
				downloadFeatures: [
					'中英文双语界面支持',
					'7大行业标准库完整访问',
					'30门专业课程资源',
					'国际项目案例库'
				],
				islogin: false,
			}
		},
		mounted() {
			this.createFloatingElements();
			this.setupIntersectionObserver();
			// 检查登录状态
			if (sessionStorage.getItem('currentUser')) {
				this.islogin = true
			} else {
				this.islogin = false
			}
		},
		methods: {
			lesaon(items) {
				console.log(items)
				if (items.title == "一键备课系统") {
					this.$router.push({
						path: '/lesson-preparation',
					});
				}
			},

			// Chat functionality
			adjustTextareaHeight(event) {
				const textarea = event.target;
				textarea.style.height = 'auto';
				textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
			},
			handleEnterKey(event) {
				if (!event.shiftKey) {
					this.sendMessage();
				}
			},
			sendMessage() {
				if (!this.islogin) {
					this.$router.push({
						path: '/',
					});
					return
				}
				const message = this.chatMessage.trim();
				if (message) {
					// 通过路由查询参数传递消息
					this.$router.push({
						path: '/ai-chat',
						query: {
							q: message
						}
					});
				} else {
					// 如果没有输入内容，直接跳转
					// this.$router.push('/ai-chat');
				}
			},
			selectQuickAction(title) {
				if (!this.islogin) {
					this.$router.push({
						path: '/',
					});
					return
				}
				// 将选中的快速问题存储到sessionStorage
				sessionStorage.setItem('initialMessage', title);
				// 跳转到AI对话页面
				this.$router.push({
					path: '/ai-chat',
					query: {
						q: title
					}
				});
			},

			scrollToWechat(event) {
				event.preventDefault();
				const element = document.getElementById('wechat-contact');
				element.scrollIntoView({
					behavior: 'smooth'
				});
			},

			// Animation methods
			createFloatingElements() {
				const container = this.$refs.floatingElements;

				const createElement = () => {
					const element = document.createElement('div');
					element.className = 'floating-element';
					element.style.left = Math.random() * 100 + '%';
					element.style.width = element.style.height = Math.random() * 40 + 20 + 'px';
					element.style.animationDelay = Math.random() * 20 + 's';
					element.style.animationDuration = Math.random() * 20 + 20 + 's';
					container.appendChild(element);

					setTimeout(() => {
						if (element.parentNode) {
							element.remove();
						}
					}, 40000);
				};

				// Create initial elements
				for (let i = 0; i < 10; i++) {
					setTimeout(createElement, i * 1000);
				}

				// Continue creating elements
				setInterval(createElement, 4000);
			},

			animateNumbers() {
				this.stats.forEach(stat => {
					const increment = stat.targetValue / 50;
					const timer = setInterval(() => {
						stat.currentValue = Math.min(stat.currentValue + increment, stat.targetValue);
						if (stat.currentValue >= stat.targetValue) {
							stat.currentValue = Math.floor(stat.targetValue);
							clearInterval(timer);
						} else {
							stat.currentValue = Math.floor(stat.currentValue);
						}
					}, 30);
				});
			},

			setupIntersectionObserver() {
				const observerOptions = {
					threshold: 0.1,
					rootMargin: '0px 0px -100px 0px'
				};

				const observer = new IntersectionObserver((entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							entry.target.style.opacity = '1';
							entry.target.style.transform = 'translateY(0)';

							// Trigger number animation when stats are visible
							if (entry.target.classList.contains('stats-grid')) {
								this.animateNumbers();
							}
						}
					});
				}, observerOptions);

				// Observe elements for animation
				this.$nextTick(() => {
					const animatedElements = this.$el.querySelectorAll(
						'.stat-card, .library-card, .feature-card, .structure-card, .flow-step, .stats-grid, .download-content'
					);
					animatedElements.forEach((el, index) => {
						el.style.opacity = '0';
						el.style.transform = 'translateY(40px)';
						el.style.transition =
							`opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`;
						observer.observe(el);
					});
				});
			}
		}
	}
</script>

<style scoped>
	/* 全局样式重置 */
	* {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
	}

	body {
		font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	/* CSS变量定义 */
	.home-page {
		--primary: #2E5CE6;
		--primary-dark: #1E3A8A;
		--primary-light: #60A5FA;
		--secondary: #10B981;
		--accent: #06B6D4;
		--purple: #8B5CF6;
		--pink: #EC4899;
		--orange: #F59E0B;
		--asean: #D97706;
		--china-red: #DC2626;
		--success: #10B981;
		--warning: #F59E0B;
		--danger: #EF4444;
		--bg-white: #FFFFFF;
		--bg-gray-50: #F9FAFB;
		--bg-gray-100: #F3F4F6;
		--bg-gray-200: #E5E7EB;
		--bg-gradient: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
		--text-primary: #111827;
		--text-secondary: #6B7280;
		--text-tertiary: #9CA3AF;
		--border-light: #E5E7EB;
		--border-medium: #D1D5DB;
		--shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
		--shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
		--shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
		--shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
		--shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
		--shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
		--radius-sm: 8px;
		--radius-md: 12px;
		--radius-lg: 16px;
		--radius-xl: 24px;
		--radius-2xl: 32px;
		--transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.home-page {
		background: var(--bg-gradient);
		color: var(--text-primary);
		line-height: 1.6;
		min-height: 100vh;
		position: relative;
		overflow-x: hidden;
	}

	/* 背景装饰 */
	.bg-decoration {
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;
		overflow: hidden;
	}

	.bg-circle {
		position: absolute;
		border-radius: 50%;
		opacity: 0.03;
	}

	.bg-circle-1 {
		width: 600px;
		height: 600px;
		background: var(--primary);
		top: -300px;
		right: -200px;
	}

	.bg-circle-2 {
		width: 400px;
		height: 400px;
		background: var(--asean);
		bottom: -200px;
		left: -100px;
	}

	.bg-circle-3 {
		width: 300px;
		height: 300px;
		background: var(--china-red);
		top: 50%;
		left: 10%;
	}

	/* Hero区域 */
	.hero-section {
		padding: 120px 0 60px;
		text-align: center;
		position: relative;
	}

	.hero-container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.hero-badge {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: white;
		padding: 8px 20px;
		border-radius: 100px;
		font-size: 14px;
		font-weight: 600;
		color: var(--primary);
		box-shadow: var(--shadow-md);
		margin-bottom: 24px;
	}

	.hero-badge-dot {
		width: 8px;
		height: 8px;
		background: var(--success);
		border-radius: 50%;
		animation: pulse 2s infinite;
	}

	@keyframes pulse {

		0%,
		100% {
			opacity: 1;
			transform: scale(1);
		}

		50% {
			opacity: 0.5;
			transform: scale(0.8);
		}
	}

	.hero-title {
		font-size: clamp(28px, 5vw, 56px);
		font-weight: 800;
		line-height: 1.1;
		margin-bottom: 24px;
		background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		letter-spacing: -0.02em;
	}

	.hero-title1 {
		font-size: clamp(20px, 3.5vw, 35px);
		margin-top: -10px;
	}

	/* 东盟国旗部分样式 */
	.asean-flags-section {
		margin: 40px 0;
		padding: 20px 0;
	}

	.flags-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 20px;
		margin-bottom: 20px;
	}

	.flags-title {
		font-size: 14px;
		font-weight: 600;
		color: var(--primary);
		text-transform: uppercase;
		letter-spacing: 1px;
	}

	.all-flags-wrapper {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: clamp(8px, 2vw, 16px);
		max-width: 1200px;
		width: 100%;
	}

	.china-flag {
		position: relative;
	}

	.flag-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: clamp(8px, 2vw, 12px);
		background: white;
		border-radius: var(--radius-md);
		box-shadow: var(--shadow-sm);
		transition: var(--transition-base);
		cursor: pointer;
		border: 1px solid var(--border-light);
		min-width: 0;
		flex: 0 0 auto;
	}

	.flag-item:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-md);
		border-color: var(--primary-light);
	}

	.china-flag {
		background: linear-gradient(135deg, #ffebee 0%, #fff3e0 100%);
		border: 2px solid var(--china-red);
	}

	.flag-img {
		width: clamp(40px, 8vw, 60px);
		height: clamp(27px, 5.3vw, 40px);
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		overflow: hidden;
		background: #f5f5f5;
	}

	.flag-img svg {
		width: 100%;
		height: 100%;
		display: block;
	}

	.flag-name {
		font-size: clamp(10px, 2.5vw, 12px);
		font-weight: 600;
		color: var(--text-primary);
		white-space: nowrap;
		margin-top: 4px;
		text-align: center;
	}

	.flag-name-en {
		font-size: clamp(8px, 2vw, 10px);
		font-weight: 400;
		color: var(--text-secondary);
		white-space: nowrap;
		text-align: center;
	}

	.flags-subtitle {
		text-align: center;
		font-size: clamp(14px, 3vw, 16px);
		color: var(--text-secondary);
		font-weight: 500;
		max-width: 600px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.hero-subtitle {
		font-size: clamp(16px, 3.5vw, 20px);
		color: var(--text-secondary);
		max-width: 700px;
		margin: 0 auto 40px;
		line-height: 1.6;
		padding: 0 20px;
	}

	.hero-subtitle1 {
		font-size: clamp(14px, 3vw, 16px);
		margin-top: -40px;
	}

	.hero-mascot {
		width: clamp(120px, 20vw, 180px);
		height: clamp(120px, 20vw, 180px);
		margin: 0 auto 40px;
		position: relative;
		animation: float 6s ease-in-out infinite;
	}

	@keyframes float {

		0%,
		100% {
			transform: translateY(0);
		}

		50% {
			transform: translateY(-20px);
		}
	}

	.mascot-container {
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, #FFE4E1 0%, #FFF7ED 50%, #E6E6FA 100%);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 20px 40px rgba(217, 119, 6, 0.2);
		position: relative;
		overflow: hidden;
	}

	.mascot-container::before {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 60%);
	}

	.mascot-text {
		font-size: clamp(40px, 10vw, 80px);
		font-weight: 800;
		background: linear-gradient(135deg, var(--china-red) 0%, var(--asean) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		z-index: 1;
		position: relative;
	}

	/* 聊天区域 */
	.chat-section {
		max-width: 720px;
		margin: 0 auto 80px;
		padding: 0 20px;
	}

	.chat-container {
		background: white;
		border-radius: var(--radius-2xl);
		box-shadow: var(--shadow-xl);
		padding: 8px;
		position: relative;
		border: 1px solid var(--border-light);
	}

	.chat-input-wrapper {
		display: flex;
		align-items: flex-end;
		gap: 12px;
	}

	.chat-input {
		flex: 1;
		min-height: 56px;
		max-height: 120px;
		padding: 16px 20px;
		border: none;
		font-size: 16px;
		font-family: inherit;
		resize: none;
		outline: none;
		color: var(--text-primary);
		background: var(--bg-gray-50);
		border-radius: var(--radius-xl);
		transition: var(--transition-base);
	}

	.chat-input:focus {
		background: white;
		box-shadow: 0 0 0 3px rgba(46, 92, 230, 0.1);
	}

	.chat-input::placeholder {
		color: var(--text-tertiary);
	}

	.send-button {
		width: 56px;
		height: 56px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
		border: none;
		border-radius: var(--radius-xl);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: var(--transition-base);
		flex-shrink: 0;
	}

	.send-button:hover {
		transform: scale(1.05);
		box-shadow: 0 10px 20px rgba(46, 92, 230, 0.3);
	}

	.send-button:active {
		transform: scale(0.95);
	}

	.send-button svg {
		width: 24px;
		height: 24px;
		fill: white;
	}

	/* 快速操作 */
	.quick-actions-section {
		max-width: 1200px;
		margin: 0 auto 80px;
		padding: 0 20px;
	}

	.section-header {
		margin-bottom: 32px;
		text-align: center;
		display: flex;
		justify-content: center;
		align-items: flex-start;
		gap: 20px;
	}

	.section-header1 {
		font-size: clamp(24px, 5vw, 30px);
		flex-shrink: 0;
	}

	.section-title {
		font-size: clamp(24px, 5vw, 32px);
		font-weight: 700;
		color: var(--text-primary);
		margin-bottom: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
	}

	.section-title1 span {
		display: block;
		text-align: center;
	}

	.section-title1 span:nth-child(2) {
		font-size: clamp(18px, 4vw, 24px);
		margin-top: -5px;
	}

	.section-subtitle {
		font-size: clamp(14px, 3vw, 18px);
		color: var(--text-secondary);
		text-align: center;
		padding: 0 10px;
	}

	.quick-actions-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 20px;
	}

	.quick-action-card {
		background: white;
		border-radius: var(--radius-lg);
		padding: 24px;
		display: flex;
		align-items: center;
		gap: 16px;
		cursor: pointer;
		transition: var(--transition-base);
		border: 1px solid var(--border-light);
		position: relative;
		overflow: hidden;
	}

	.quick-action-card::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		opacity: 0;
		transition: var(--transition-base);
	}

	.quick-action-card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-lg);
		border-color: var(--primary-light);
	}

	.quick-action-card:hover::before {
		opacity: 0.05;
	}

	.quick-action-icon {
		width: 48px;
		height: 48px;
		background: var(--bg-gray-100);
		border-radius: var(--radius-md);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		flex-shrink: 0;
		position: relative;
		z-index: 1;
	}

	.quick-action-text {
		flex: 1;
		position: relative;
		z-index: 1;
	}

	.quick-action-title {
		font-weight: 600;
		font-size: clamp(14px, 3vw, 16px);
		color: var(--text-primary);
		line-height: 1.4;
		margin-bottom: 4px;
	}

	.quick-action-title1 {
		font-size: clamp(11px, 2.5vw, 12px);
		margin-bottom: 0;
	}

	/* 功能网格 */
	.features-section {
		background: white;
		padding: 80px 0;
		position: relative;
		border-top: 1px solid var(--border-light);
	}

	.features-container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.features-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 24px;
		margin-top: 48px;
	}

	.feature-card {
		background: var(--bg-gray-50);
		border-radius: var(--radius-xl);
		padding: 32px;
		transition: var(--transition-base);
		border: 1px solid var(--border-light);
		position: relative;
		overflow: hidden;
		cursor: pointer;
	}

	.feature-card::before {
		content: '';
		position: absolute;
		top: -2px;
		left: -2px;
		right: -2px;
		bottom: -2px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		border-radius: var(--radius-xl);
		opacity: 0;
		transition: var(--transition-base);
		z-index: -1;
	}

	.feature-card:hover {
		transform: translateY(-4px);
		background: white;
		box-shadow: var(--shadow-xl);
	}

	.feature-card:hover::before {
		opacity: 1;
	}

	.feature-card.highlight {
		background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%);
		border: 2px solid var(--china-red);
	}

	.feature-icon {
		width: 64px;
		height: 64px;
		background: white;
		border-radius: var(--radius-lg);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 32px;
		margin-bottom: 20px;
		box-shadow: var(--shadow-md);
	}

	.feature-title {
		font-size: clamp(16px, 3.5vw, 20px);
		font-weight: 700;
		margin-bottom: 12px;
		color: var(--text-primary);
	}

	.feature-title1 {
		font-size: clamp(13px, 3vw, 16px);
		margin-top: -8px;
	}

	.feature-description {
		font-size: clamp(14px, 3vw, 16px);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	.feature-tag {
		display: inline-block;
		background: var(--success);
		color: white;
		padding: 6px 16px;
		border-radius: 100px;
		font-size: clamp(12px, 2.5vw, 14px);
		font-weight: 600;
		margin-top: 16px;
	}

	/* 专家系统区域 */
	.expert-system-section {
		padding: 80px 0;
		background: var(--bg-gradient);
		position: relative;
	}

	.expert-header {
		text-align: center;
		margin-bottom: 60px;
		padding: 0 20px;
	}

	.expert-title {
		font-size: clamp(28px, 6vw, 48px);
		font-weight: 800;
		line-height: 1.2;
		margin-bottom: 20px;
		color: var(--text-primary);
	}

	.expert-title1 {
		font-size: clamp(20px, 4.5vw, 35px);
		margin-top: -10px;
	}

	.expert-title-gradient {
		background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		font-size: clamp(24px, 5vw, 40px);
		font-weight: 800;
		display: block;
		margin-bottom: 20px;
	}

	.expert-subtitle {
		font-size: clamp(16px, 3.5vw, 20px);
		color: var(--text-secondary);
		font-weight: 500;
	}

	.version-badge {
		display: inline-block;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		color: white;
		padding: 6px 20px;
		border-radius: 100px;
		font-size: clamp(12px, 2.5vw, 14px);
		font-weight: 700;
		margin-left: 12px;
	}

	/* 统计卡片 */
	.stats-container {
		max-width: 1200px;
		margin: 0 auto 80px;
		padding: 0 20px;
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 24px;
	}

	.stat-card {
		background: white;
		border-radius: var(--radius-xl);
		padding: 32px;
		text-align: center;
		box-shadow: var(--shadow-md);
		transition: var(--transition-base);
		border: 1px solid var(--border-light);
		position: relative;
		overflow: hidden;
	}

	.stat-card::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: linear-gradient(90deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		transform: scaleX(0);
		transition: var(--transition-base);
	}

	.stat-card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-xl);
	}

	.stat-card:hover::after {
		transform: scaleX(1);
	}

	.stat-number {
		font-size: clamp(32px, 8vw, 48px);
		font-weight: 800;
		background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		line-height: 1;
		margin-bottom: 8px;
	}

	.stat-label {
		color: var(--text-secondary);
		font-size: clamp(14px, 3vw, 16px);
		font-weight: 500;
	}

	/* 架构卡片 */
	.architecture-container {
		max-width: 1400px;
		margin: 0 auto 80px;
		padding: 0 20px;
	}

	.architecture-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
		gap: 24px;
	}

	.library-card {
		background: white;
		border-radius: var(--radius-xl);
		padding: 40px 32px;
		transition: var(--transition-base);
		border: 1px solid var(--border-light);
		position: relative;
		overflow: hidden;
	}

	.library-card:hover {
		transform: translateY(-8px);
		box-shadow: var(--shadow-2xl);
	}

	.library-icon {
		width: 72px;
		height: 72px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		border-radius: var(--radius-lg);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 36px;
		margin-bottom: 24px;
		box-shadow: 0 10px 20px rgba(46, 92, 230, 0.2);
	}

	.library-title {
		font-size: clamp(18px, 4vw, 24px);
		font-weight: 700;
		margin-bottom: 16px;
		color: var(--text-primary);
	}

	.library-title1 {
		font-size: clamp(14px, 3vw, 16px);
		margin-top: -8px;
	}

	.library-description {
		font-size: clamp(14px, 3vw, 16px);
		line-height: 1.6;
		color: var(--text-secondary);
	}

	/* 数据流 */
	.data-flow-section {
		background: white;
		padding: 80px 0;
		border-top: 1px solid var(--border-light);
		border-bottom: 1px solid var(--border-light);
	}

	.data-flow-container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.flow-title {
		text-align: center;
		font-size: clamp(24px, 5vw, 40px);
		font-weight: 800;
		margin-bottom: 60px;
		background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}

	.flow-title1 {
		font-size: clamp(18px, 4vw, 30px);
		margin-top: -30px;
	}

	.flow-diagram {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 24px;
		position: relative;
		flex-wrap: wrap;
	}

	.flow-step {
		flex: 1;
		min-width: 200px;
		background: var(--bg-gray-50);
		border-radius: var(--radius-lg);
		padding: 32px 24px;
		text-align: center;
		transition: var(--transition-base);
		border: 2px solid transparent;
		position: relative;
	}

	.flow-step:hover {
		transform: translateY(-4px);
		background: white;
		border-color: var(--primary-light);
		box-shadow: var(--shadow-lg);
	}

	.flow-step-icon {
		font-size: clamp(32px, 6vw, 48px);
		margin-bottom: 16px;
		display: block;
	}

	.flow-step-title {
		font-size: clamp(16px, 3.5vw, 20px);
		font-weight: 700;
		margin-bottom: 8px;
		color: var(--text-primary);
	}

	.flow-step-title1 {
		font-size: clamp(12px, 2.5vw, 14px);
		margin-top: -5px;
	}

	.flow-step-desc {
		font-size: clamp(12px, 2.5vw, 14px);
		color: var(--text-secondary);
		line-height: 1.5;
	}

	.flow-arrow {
		width: 40px;
		height: 2px;
		background: linear-gradient(90deg, var(--primary) 0%, var(--asean) 100%);
		position: relative;
		flex-shrink: 0;
	}

	.flow-arrow::after {
		content: '';
		position: absolute;
		right: -8px;
		top: -6px;
		width: 0;
		height: 0;
		border-left: 14px solid var(--asean);
		border-top: 7px solid transparent;
		border-bottom: 7px solid transparent;
	}

	/* 详细结构 */
	.structure-section {
		padding: 80px 0;
		background: var(--bg-gradient);
	}

	.structure-container {
		max-width: 1400px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.structure-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
		gap: 32px;
		margin-top: 48px;
	}

	.structure-card {
		background: white;
		border-radius: var(--radius-xl);
		padding: 40px;
		box-shadow: var(--shadow-lg);
		transition: var(--transition-base);
		border: 1px solid var(--border-light);
	}

	.structure-card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-2xl);
	}

	.structure-header {
		font-size: clamp(18px, 4vw, 24px);
		font-weight: 700;
		color: var(--primary);
		margin-bottom: 24px;
		padding-bottom: 16px;
		border-bottom: 2px solid var(--bg-gray-100);
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}

	.subsection {
		margin: 12px 0;
		padding: 16px 20px;
		background: var(--bg-gray-50);
		border-radius: var(--radius-md);
		border-left: 4px solid transparent;
		transition: var(--transition-base);
		font-size: clamp(13px, 2.8vw, 16px);
		color: var(--text-primary);
		cursor: pointer;
	}

	.subsection:hover {
		background: white;
		border-left-color: var(--primary);
		transform: translateX(8px);
		box-shadow: var(--shadow-md);
	}

	/* 下载区域 */
	.download-section {
		background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
		padding: 100px 0;
		position: relative;
		overflow: hidden;
	}

	.download-section::before {
		content: '';
		position: absolute;
		top: -50%;
		left: -50%;
		width: 200%;
		height: 200%;
		background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
		animation: float-element 30s infinite linear;
	}

	.download-container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
		text-align: center;
		position: relative;
		z-index: 1;
	}

	.download-title {
		font-size: clamp(28px, 6vw, 48px);
		font-weight: 800;
		color: white;
		margin-bottom: 24px;
		letter-spacing: -0.02em;
	}

	.download-title1 {
		font-size: clamp(20px, 4vw, 30px);
		margin-top: -20px;
	}

	.download-subtitle {
		font-size: clamp(16px, 4vw, 24px);
		color: rgba(255, 255, 255, 0.9);
		margin-bottom: 48px;
		font-weight: 500;
	}

	.download-subtitle1 {
		margin-top: -30px;
	}

	.download-content {
		background: white;
		border-radius: var(--radius-2xl);
		padding: clamp(30px, 6vw, 60px);
		max-width: 800px;
		margin: 0 auto;
		box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
		position: relative;
	}

	.download-badge {
		position: absolute;
		top: -20px;
		right: clamp(20px, 5vw, 40px);
		background: var(--success);
		color: white;
		padding: 8px 24px;
		border-radius: 100px;
		font-weight: 700;
		font-size: clamp(12px, 2.5vw, 16px);
		box-shadow: var(--shadow-lg);
	}

	.download-features {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 24px;
		margin-bottom: 48px;
	}

	.download-feature {
		display: flex;
		align-items: center;
		gap: 12px;
		text-align: left;
	}

	.download-feature-icon {
		width: 32px;
		height: 32px;
		background: linear-gradient(135deg, var(--primary-light) 0%, var(--asean) 100%);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 16px;
		flex-shrink: 0;
	}

	.download-feature-text {
		font-size: clamp(14px, 3vw, 16px);
		color: var(--text-primary);
		font-weight: 500;
	}

	.download-cta {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.download-button {
		display: inline-flex;
		align-items: center;
		gap: 12px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		color: white;
		padding: clamp(16px, 3vw, 20px) clamp(32px, 6vw, 48px);
		border-radius: 100px;
		font-size: clamp(16px, 3.5vw, 20px);
		font-weight: 700;
		text-decoration: none;
		transition: var(--transition-base);
		box-shadow: 0 10px 30px rgba(46, 92, 230, 0.3);
		cursor: pointer;
	}

	.download-button:hover {
		transform: translateY(-4px);
		box-shadow: 0 20px 40px rgba(46, 92, 230, 0.4);
	}

	.download-button-icon {
		font-size: clamp(18px, 4vw, 24px);
	}

	.wechat-info {
		background: var(--bg-gray-50);
		border-radius: var(--radius-xl);
		padding: 32px;
		display: flex;
		align-items: center;
		gap: 32px;
		margin-top: 32px;
		width: 100%;
	}

	.wechat-qr {
		width: clamp(120px, 20vw, 160px);
		height: clamp(120px, 20vw, 160px);
		background: white;
		border-radius: var(--radius-lg);
		padding: 12px;
		box-shadow: var(--shadow-md);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.wechat-qr img {
		max-width: 100%;
		max-height: 100%;
	}

	.wechat-qr-placeholder {
		width: 100%;
		height: 100%;
		border-radius: var(--radius-md);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		color: var(--text-tertiary);
	}

	.wechat-text {
		flex: 1;
		text-align: left;
	}

	.wechat-title {
		font-size: clamp(16px, 3.5vw, 20px);
		font-weight: 700;
		color: var(--text-primary);
		margin-bottom: 8px;
	}

	.wechat-id {
		font-size: clamp(13px, 2.8vw, 15px);
		font-weight: 700;
		color: var(--primary);
		margin-bottom: 12px;
		letter-spacing: 0.5px;
	}

	.wechat-desc {
		font-size: clamp(14px, 3vw, 16px);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* 浮动元素 */
	.floating-elements {
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		pointer-events: none;
		z-index: -1;
		overflow: hidden;
	}

	.floating-element {
		position: absolute;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
		opacity: 0.03;
		animation: float-element 20s infinite linear;
	}

	@keyframes float-element {
		0% {
			transform: translateY(100vh) rotate(0deg);
		}

		100% {
			transform: translateY(-100px) rotate(360deg);
		}
	}

	/* 响应式媒体查询 */
	@media (max-width: 1200px) {
		.section-header1 {
			position: static;
			transform: none;
		}

		.section-header {
			flex-direction: column;
			gap: 10px;
		}
	}

	@media (max-width: 1024px) {
		.hero-section {
			padding: 100px 0 40px;
		}

		.architecture-grid {
			grid-template-columns: repeat(2, 1fr);
		}

		.all-flags-wrapper {
			gap: 12px;
		}

		.wechat-info {
			flex-direction: column;
			text-align: center;
		}

		.wechat-text {
			text-align: center;
		}
	}

	@media (max-width: 768px) {
		.hero-section {
			padding: 80px 0 40px;
		}

		.stats-grid,
		.features-grid,
		.architecture-grid,
		.structure-grid {
			grid-template-columns: 1fr;
		}

		.flow-diagram {
			flex-direction: column;
			gap: 20px;
		}

		.flow-arrow {
			transform: rotate(90deg);
			width: 30px;
			margin: 10px 0;
		}

		.flow-arrow::after {
			display: none;
		}

		.chat-input {
			font-size: 16px;
		}

		.download-content {
			padding: 30px 20px;
		}

		.download-features {
			grid-template-columns: 1fr;
		}

		.all-flags-wrapper {
			gap: 8px;
		}

		.flag-item {
			padding: 6px;
		}
	}

	@media (max-width: 480px) {
		.hero-section {
			padding: 60px 0 30px;
		}

		.quick-actions-grid {
			grid-template-columns: 1fr;
			gap: 16px;
		}

		.quick-action-card {
			padding: 20px;
			gap: 12px;
		}

		.quick-action-icon {
			width: 40px;
			height: 40px;
			font-size: 20px;
		}

		.feature-card {
			padding: 24px;
		}

		.feature-icon {
			width: 48px;
			height: 48px;
			font-size: 24px;
		}

		.library-card {
			padding: 24px 20px;
		}

		.library-icon {
			width: 56px;
			height: 56px;
			font-size: 28px;
		}

		.structure-card {
			padding: 24px;
		}

		.subsection {
			padding: 12px 16px;
			font-size: 14px;
		}

		.wechat-info {
			padding: 24px;
			gap: 20px;
		}

		.all-flags-wrapper {
			gap: 6px;
		}

		.flag-item {
			padding: 4px;
		}
	}

	@media (max-width: 360px) {
		.hero-container,
		.chat-section,
		.quick-actions-section,
		.features-container,
		.stats-container,
		.architecture-container,
		.data-flow-container,
		.structure-container,
		.download-container {
			padding-left: 16px;
			padding-right: 16px;
		}

		.all-flags-wrapper {
			gap: 4px;
		}

		.flag-item {
			padding: 3px;
		}

		.flag-name {
			font-size: 9px;
		}

		.flag-name-en {
			font-size: 7px;
		}
	}
</style>